export function phoneNumberIsValid(phone: string): boolean {
    const phoneRegEx = new RegExp('^[+]?[(]?[0-9]{3}[)]?[-s.]?[0-9]{3}[-s.]?[0-9]{4,6}$');

    return phoneRegEx.test(phone);
}

export function emailAddressIsValid(email: string): boolean {
    const emailRegEx = new RegExp('^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$');

    return emailRegEx.test(email);
}

/**
 * Verbatim copies of the two patterns ajv-formats (2.1.1) uses for JSON Schema `format: "email"`.
 * The surveys service validates surveyContactMethods[].emailAddress with that format, so these are
 * the real acceptance criteria for anything we put in a batch payload. Which one runs depends on the
 * mode the service registers ajv-formats with, so callers require BOTH: `emailAddressIsValid` above
 * is far looser than either, and an address it wrongly accepts fails schema validation and 400s the
 * ENTIRE batch request (taking up to 49 unrelated surveys down with it), while one we wrongly reject
 * costs only that survey's email contact method.
 *
 * Source: node_modules/ajv-formats/dist/formats.js — fastFormats.email / fullFormats.email.
 */
export const AJV_EMAIL_FAST =
    /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/i;
export const AJV_EMAIL_FULL =
    /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

/** Every character ajv's email format can accept. Anything else is reported as an offender. */
const AJV_EMAIL_CHARSET = /[A-Za-z0-9.!#$%&'*+/=?^_`{|}~@-]/;

/**
 * Render an address as a safe-to-log skeleton: every alphanumeric becomes 'x', punctuation and
 * structure are preserved verbatim, and anything outside printable ASCII becomes a \uXXXX escape.
 *
 * This is deliberately not the address. Email addresses are patient PII and must not reach the log
 * pipeline, but every defect we need to diagnose lives in the structure rather than the letters:
 *   'a@b.com.'          -> "x@x.xxx."          (trailing dot)
 *   'a@b.com' + U+200B  -> "x@x.xxx​"     (zero-width space)
 *   'a@my_domain.com'   -> "x@xx_xxxxxx.xxx"   (underscore in domain)
 *   Cyrillic О in .COM  -> "x@x.xОx"      (homoglyph)
 *
 * Note that JSON.stringify would NOT be sufficient even for the escaping half: it escapes only
 * control characters below 0x20, quotes, and backslashes, so a zero-width space or a homoglyph
 * passes through untouched and stays invisible — the exact failure this diagnostic exists to expose.
 */
function maskedShapeOf(value: string): string {
    let shape = '';
    for (const character of value) {
        const codePoint = character.codePointAt(0) ?? 0;
        if (/[A-Za-z0-9]/.test(character)) {
            shape += 'x';
        } else if (codePoint >= 0x20 && codePoint <= 0x7e) {
            shape += character;
        } else if (codePoint > 0xffff) {
            shape += `\\u{${codePoint.toString(16).toUpperCase()}}`;
        } else {
            shape += `\\u${codePoint.toString(16).toUpperCase().padStart(4, '0')}`;
        }
    }
    return shape;
}

export interface EmailDiagnostic {
    /**
     * Safe-to-log skeleton of the address — alphanumerics masked, structure and invisible characters
     * preserved. Never contains the address itself; see maskedShapeOf.
     */
    maskedShape: string;
    chars: number;
    bytes: number;
    passesLoose: boolean;
    passesAjvFast: boolean;
    passesAjvFull: boolean;
    /** Our guard accepts it but ajv will not — precisely the case that 400s a whole batch. */
    wouldFailService: boolean;
    /** Per-character detail, e.g. 'pos 22 U+200B "​"'. Empty when the cause is structural. */
    offenders: string[];
}

/**
 * Test an address against both our loose guard and ajv's real patterns, and describe the result in a
 * form that survives a log pipeline WITHOUT carrying the address itself, which is patient PII.
 * `maskedShape` is the important field: it turns otherwise-invisible characters (zero-width spaces,
 * homoglyphs, stray control codes) and easily-missed ones (a trailing dot) into something readable
 * in Datadog, while masking the letters and digits that identify the patient.
 *
 * `offenders` reports disallowed characters only. A structurally invalid address made entirely of
 * legal characters — 'a@b.com.', 'a..b@c.com', 'a@my_domain.com' — yields an empty list, so read the
 * three pass* booleans for the verdict and use `maskedShape` to see why.
 */
export function describeEmailAddress(rawEmail: string): EmailDiagnostic {
    const trimmed = rawEmail.trim();
    const passesLoose = emailAddressIsValid(trimmed);
    const passesAjvFast = AJV_EMAIL_FAST.test(trimmed);
    const passesAjvFull = AJV_EMAIL_FULL.test(trimmed);

    const offenders: string[] = [];
    for (let i = 0; i < trimmed.length; i += 1) {
        const character = trimmed[i];
        if (!AJV_EMAIL_CHARSET.test(character)) {
            const codePoint = (trimmed.codePointAt(i) ?? 0).toString(16).toUpperCase().padStart(4, '0');
            offenders.push(`pos ${i + 1} U+${codePoint} ${JSON.stringify(character)}`);
        }
    }

    return {
        maskedShape: maskedShapeOf(rawEmail),
        chars: rawEmail.length,
        bytes: Buffer.byteLength(rawEmail, 'utf8'),
        passesLoose,
        passesAjvFast,
        passesAjvFull,
        wouldFailService: passesLoose && !(passesAjvFast && passesAjvFull),
        offenders,
    };
}

/** True only when the surveys service will accept the address under either ajv-formats mode. */
export function emailAddressIsAcceptedBySurveysService(email: string | null | undefined): boolean {
    if (!email) {
        return false;
    }
    const trimmed = email.trim();
    if (trimmed.length > 254) {
        return false; // RFC 5321 cap; neither ajv pattern bounds total length
    }
    return AJV_EMAIL_FAST.test(trimmed) && AJV_EMAIL_FULL.test(trimmed);
}
